import XLSX from 'xlsx-js-style';
import { format, parseISO, eachDayOfInterval, differenceInDays } from 'date-fns';
import { getWeeksBetween, hoursToWorkingDays } from './dateUtils';

// Export Gantt chart to Excel with real cell coloring
export const exportGanttToExcel = (projects, tasks, resources, viewMode = 'week', startDate = '2026-01-01', endDate = '2026-12-31', macroProjectName = 'Tutti', holidays = {}) => {
  const workbook = XLSX.utils.book_new();
  
  // ============== Sheet 1: Gantt Overview ==============
  const ganttData = [];
  ganttData.push(['Progetto', 'Task', 'Data Inizio', 'Data Fine', 'Avanzamento (%)', 'Risorse Assegnate']);
  
  for (let p = 0; p < projects.length; p++) {
    const project = projects[p];
    ganttData.push([
      project.name,
      '',
      format(parseISO(project.startDate), 'dd/MM/yyyy'),
      format(parseISO(project.endDate), 'dd/MM/yyyy'),
      '',
      ''
    ]);
    
    const projectTasks = tasks.filter(function(t) { return t.projectId === project.id; });
    for (let t = 0; t < projectTasks.length; t++) {
      const task = projectTasks[t];
      const assignedResources = [];
      for (let r = 0; r < task.resourceAllocations.length; r++) {
        const ra = task.resourceAllocations[r];
        const resource = resources.find(function(res) { return res.id === ra.resourceId; });
        if (resource) assignedResources.push(resource.name);
      }
      
      ganttData.push([
        '',
        task.name,
        format(parseISO(task.startDate), 'dd/MM/yyyy'),
        format(parseISO(task.endDate), 'dd/MM/yyyy'),
        task.progress,
        assignedResources.join(', ')
      ]);
    }
  }
  
  const ganttSheet = XLSX.utils.aoa_to_sheet(ganttData);
  
  // Style header row
  const headerStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "4F46E5" } },
    alignment: { horizontal: "center" }
  };
  
  for (let c = 0; c < 6; c++) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: c });
    if (ganttSheet[cellRef]) {
      ganttSheet[cellRef].s = headerStyle;
    }
  }
  
  ganttSheet['!cols'] = [
    { wch: 25 },
    { wch: 30 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 40 },
  ];
  XLSX.utils.book_append_sheet(workbook, ganttSheet, 'Gantt');
  
  // ============== Sheet 2: Resources ==============
  const resourcesData = [['Nome', 'Avatar']];
  for (let r = 0; r < resources.length; r++) {
    const resource = resources[r];
    resourcesData.push([resource.name, resource.avatar || '']);
  }
  
  const resourcesSheet = XLSX.utils.aoa_to_sheet(resourcesData);
  resourcesSheet['!cols'] = [{ wch: 30 }, { wch: 60 }];
  
  for (let c = 0; c < 2; c++) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: c });
    if (resourcesSheet[cellRef]) {
      resourcesSheet[cellRef].s = headerStyle;
    }
  }
  
  XLSX.utils.book_append_sheet(workbook, resourcesSheet, 'Risorse');
  
  // ============== Sheet 3: Gantt Visual (with cell coloring) ==============
  const visualData = [];
  
  // Generate all days in range
  const rangeStart = parseISO(startDate);
  const rangeEnd = parseISO(endDate);
  const allDays = eachDayOfInterval({ start: rangeStart, end: rangeEnd });
  
  // Group days by month for header
  const months = {};
  for (let d = 0; d < allDays.length; d++) {
    const day = allDays[d];
    const monthKey = format(day, 'MMM yy');
    if (!months[monthKey]) {
      months[monthKey] = { start: d, count: 0 };
    }
    months[monthKey].count++;
  }
  
  // Month header row
  const monthRow = ['Progetto/Task'];
  const monthKeys = Object.keys(months);
  for (let m = 0; m < monthKeys.length; m++) {
    const monthKey = monthKeys[m];
    const monthInfo = months[monthKey];
    monthRow.push(monthKey);
    for (let i = 1; i < monthInfo.count; i++) {
      monthRow.push('');
    }
  }
  visualData.push(monthRow);
  
  // Day header row
  const dayRow = [''];
  for (let d = 0; d < allDays.length; d++) {
    dayRow.push(format(allDays[d], 'd'));
  }
  visualData.push(dayRow);
  
  // Store cell styling info
  const cellStyles = [];
  
  // Build rows for projects and tasks
  for (let p = 0; p < projects.length; p++) {
    const project = projects[p];
    const projectRow = [project.name];
    const rowIndex = visualData.length;
    
    // Calculate project bar positions
    const projStart = parseISO(project.startDate);
    const projEnd = parseISO(project.endDate);
    const projStartOffset = Math.max(0, differenceInDays(projStart, rangeStart));
    const projEndOffset = Math.min(allDays.length - 1, differenceInDays(projEnd, rangeStart));
    
    for (let d = 0; d < allDays.length; d++) {
      if (d >= projStartOffset && d <= projEndOffset) {
        projectRow.push('');
        cellStyles.push({
          row: rowIndex,
          col: d + 1,
          color: project.color,
          type: 'project'
        });
      } else {
        projectRow.push('');
      }
    }
    visualData.push(projectRow);
    
    // Tasks for this project
    const projectTasks = tasks.filter(function(t) { return t.projectId === project.id; });
    for (let t = 0; t < projectTasks.length; t++) {
      const task = projectTasks[t];
      const taskRow = ['  ' + task.name];
      const taskRowIndex = visualData.length;
      
      const taskStart = parseISO(task.startDate);
      const taskEnd = parseISO(task.endDate);
      const taskStartOffset = Math.max(0, differenceInDays(taskStart, rangeStart));
      const taskEndOffset = Math.min(allDays.length - 1, differenceInDays(taskEnd, rangeStart));
      const taskDuration = taskEndOffset - taskStartOffset + 1;
      const progressCells = Math.round((task.progress / 100) * taskDuration);
      
      for (let d = 0; d < allDays.length; d++) {
        if (d >= taskStartOffset && d <= taskEndOffset) {
          const cellIndex = d - taskStartOffset;
          taskRow.push('');
          
          if (cellIndex < progressCells) {
            cellStyles.push({
              row: taskRowIndex,
              col: d + 1,
              color: project.color,
              type: 'task-complete'
            });
          } else {
            cellStyles.push({
              row: taskRowIndex,
              col: d + 1,
              color: project.color,
              type: 'task-remaining'
            });
          }
        } else {
          taskRow.push('');
        }
      }
      visualData.push(taskRow);
    }
  }
  
  const visualSheet = XLSX.utils.aoa_to_sheet(visualData);
  
  // Set column widths
  const visualCols = [{ wch: 30 }];
  for (let d = 0; d < allDays.length; d++) {
    visualCols.push({ wch: 3 });
  }
  visualSheet['!cols'] = visualCols;
  
  // Merge cells for month headers
  const merges = [];
  let colIndex = 1;
  for (let m = 0; m < monthKeys.length; m++) {
    const monthKey = monthKeys[m];
    const monthInfo = months[monthKey];
    if (monthInfo.count > 1) {
      merges.push({
        s: { r: 0, c: colIndex },
        e: { r: 0, c: colIndex + monthInfo.count - 1 }
      });
    }
    colIndex += monthInfo.count;
  }
  visualSheet['!merges'] = merges;
  
  // Style header rows
  for (let c = 0; c <= allDays.length; c++) {
    const monthCellRef = XLSX.utils.encode_cell({ r: 0, c: c });
    const dayCellRef = XLSX.utils.encode_cell({ r: 1, c: c });
    
    if (visualSheet[monthCellRef]) {
      visualSheet[monthCellRef].s = {
        font: { bold: true, sz: 10 },
        fill: { fgColor: { rgb: "E5E7EB" } },
        alignment: { horizontal: "center" },
        border: {
          bottom: { style: "thin", color: { rgb: "D1D5DB" } }
        }
      };
    }
    
    if (visualSheet[dayCellRef]) {
      visualSheet[dayCellRef].s = {
        font: { sz: 9 },
        fill: { fgColor: { rgb: "F3F4F6" } },
        alignment: { horizontal: "center" }
      };
    }
  }
  
  // Apply cell colors with REAL background fills
  for (let i = 0; i < cellStyles.length; i++) {
    const style = cellStyles[i];
    const cellRef = XLSX.utils.encode_cell({ r: style.row, c: style.col });
    
    if (!visualSheet[cellRef]) {
      visualSheet[cellRef] = { t: 's', v: '' };
    }
    
    const hexColor = style.color.replace('#', '').toUpperCase();
    
    if (style.type === 'project') {
      visualSheet[cellRef].s = {
        fill: { fgColor: { rgb: hexColor } },
        border: {
          top: { style: "thin", color: { rgb: hexColor } },
          bottom: { style: "thin", color: { rgb: hexColor } }
        }
      };
    } else if (style.type === 'task-complete') {
      visualSheet[cellRef].s = {
        fill: { fgColor: { rgb: hexColor } }
      };
    } else if (style.type === 'task-remaining') {
      // Lighter shade for remaining work
      const r = parseInt(hexColor.substring(0, 2), 16);
      const g = parseInt(hexColor.substring(2, 4), 16);
      const b = parseInt(hexColor.substring(4, 6), 16);
      const lighterR = Math.min(255, Math.floor(r + (255 - r) * 0.6)).toString(16).padStart(2, '0');
      const lighterG = Math.min(255, Math.floor(g + (255 - g) * 0.6)).toString(16).padStart(2, '0');
      const lighterB = Math.min(255, Math.floor(b + (255 - b) * 0.6)).toString(16).padStart(2, '0');
      const lighterHex = (lighterR + lighterG + lighterB).toUpperCase();
      
      visualSheet[cellRef].s = {
        fill: { fgColor: { rgb: lighterHex } }
      };
    }
  }
  
  XLSX.utils.book_append_sheet(workbook, visualSheet, 'Gantt Visivo');
  
  // ============== Sheet 4: Resource Workload ==============
  const resourceData = [];
  
  const allWeeks = new Set();
  for (let t = 0; t < tasks.length; t++) {
    const task = tasks[t];
    const weeks = getWeeksBetween(task.startDate, task.endDate);
    for (let w = 0; w < weeks.length; w++) {
      allWeeks.add(weeks[w]);
    }
  }
  const sortedWeeks = Array.from(allWeeks).sort();
  
  const weekLabels = sortedWeeks.map(function(w) { return w.replace('W', 'Sett. '); });
  resourceData.push(['Risorsa'].concat(weekLabels));
  
  // Track overloaded cells
  const overloadCells = [];
  
  for (let r = 0; r < resources.length; r++) {
    const resource = resources[r];
    const row = [resource.name];
    const rowIndex = resourceData.length;
    const resourceHolidays = holidays[resource.id] || {};

    for (let w = 0; w < sortedWeeks.length; w++) {
      const weekKey = sortedWeeks[w];
      let taskHours = 0;

      for (let t = 0; t < tasks.length; t++) {
        const task = tasks[t];
        const allocation = task.resourceAllocations.find(function(ra) { return ra.resourceId === resource.id; });
        if (allocation && allocation.weeklyHours[weekKey]) {
          taskHours += allocation.weeklyHours[weekKey];
        }
      }

      // Compute holiday hours for this week (same ISO week algorithm as ResourceGrid)
      let holidayHours = 0;
      const wparts = weekKey.split('-W');
      const wyear = parseInt(wparts[0]);
      const wnum = parseInt(wparts[1]);
      const jan4 = new Date(wyear, 0, 4);
      const jan4Day = jan4.getDay();
      const jan4Monday = new Date(wyear, 0, 4 - (jan4Day === 0 ? 6 : jan4Day - 1));
      const weekStart = new Date(jan4Monday);
      weekStart.setDate(jan4Monday.getDate() + (wnum - 1) * 7);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });
      for (let d = 0; d < weekDays.length; d++) {
        const dateStr = format(weekDays[d], 'yyyy-MM-dd');
        if (resourceHolidays[dateStr]) {
          holidayHours += resourceHolidays[dateStr];
        }
      }

      const totalHours = taskHours + holidayHours;
      const workingDays = hoursToWorkingDays(taskHours);
      row.push(workingDays > 0 ? workingDays.toFixed(1) : '');

      if (totalHours > 40) {
        overloadCells.push({ row: rowIndex, col: w + 1 });
      }
    }

    resourceData.push(row);
  }
  
  const resourceSheet = XLSX.utils.aoa_to_sheet(resourceData);
  resourceSheet['!cols'] = [{ wch: 20 }].concat(sortedWeeks.map(function() { return { wch: 12 }; }));
  
  // Style header row
  for (let c = 0; c <= sortedWeeks.length; c++) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: c });
    if (resourceSheet[cellRef]) {
      resourceSheet[cellRef].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4F46E5" } },
        alignment: { horizontal: "center" }
      };
    }
  }
  
  // Mark overloaded cells with red background
  for (let i = 0; i < overloadCells.length; i++) {
    const cell = overloadCells[i];
    const cellRef = XLSX.utils.encode_cell({ r: cell.row, c: cell.col });
    if (resourceSheet[cellRef]) {
      resourceSheet[cellRef].s = {
        fill: { fgColor: { rgb: "FEE2E2" } },
        font: { color: { rgb: "DC2626" }, bold: true },
        alignment: { horizontal: "center" }
      };
    }
  }
  
  XLSX.utils.book_append_sheet(workbook, resourceSheet, 'Carico Risorse');
  
  // Download the file - include macro project name in filename
  const sanitizedName = macroProjectName.replace(/[^a-zA-Z0-9]/g, '_');
  const fileName = 'InstantGantt_' + sanitizedName + '_' + format(new Date(), 'yyyy-MM-dd_HH-mm') + '.xlsx';
  XLSX.writeFile(workbook, fileName);
};
